import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Search, Calendar, User, ArrowRight, Clock, Tag, 
  Home, Building, TrendingUp, FileText, PoundSterling
} from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Static blog posts data - used as fallback if no dynamic posts
export const staticBlogPosts = [
  // LANDLORD POSTS (7)
  {
    id: 'how-to-maximize-rental-income-harrow-2025',
    title: 'How to Maximize Your Rental Income in Harrow: 2025 Guide',
    excerpt: 'Discover proven strategies to increase your rental yield in Harrow. From property upgrades to pricing strategies, learn how top landlords are earning 15-20% more.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-02-05',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80',
    tags: ['Rental Income', 'Harrow', 'Landlord Tips', 'Property Investment'],
    featured: true,
    content: `
## Why Harrow is a Rental Hotspot in 2025

Harrow has become one of London's most sought-after rental markets, with average yields of 4.5-5.5% - significantly higher than many Central London areas. Here's how to maximize your returns.

### 1. Price Your Property Correctly

The most common mistake landlords make is overpricing. Our data shows that properties priced 5% below market rate rent 3x faster and have 40% lower void periods.

**Current Average Rents in Harrow (2025):**
- 1 Bed Flat: £1,200-£1,400/month
- 2 Bed Flat: £1,400-£1,700/month
- 3 Bed House: £1,800-£2,200/month
- 4 Bed House: £2,200-£2,800/month

### 2. Invest in High-ROI Upgrades

Not all upgrades are worth it. Focus on these proven winners:

| Upgrade | Cost | Rent Increase | ROI |
|---------|------|---------------|-----|
| Modern Kitchen | £3,000-£5,000 | £100-£150/mo | 24-36% |
| New Bathroom | £2,000-£4,000 | £75-£100/mo | 22-30% |
| Fresh Paint | £500-£1,000 | £50-£75/mo | 60-90% |
| New Flooring | £1,500-£3,000 | £50-£100/mo | 40-50% |

### 3. Professional Photography is Non-Negotiable

Properties with professional photos:
- Get 118% more views
- Rent 32% faster
- Achieve 8% higher rents

At Harry & Jacobs, we include professional photography with all our management packages.

### 4. Target the Right Tenant Demographic

Harrow attracts:
- Young professionals commuting to Central London
- Families seeking good schools (John Lyon, Harrow School catchment)
- Healthcare workers (Northwick Park Hospital nearby)

Tailor your property to these groups for faster lets.

### 5. Reduce Void Periods

The average void period in Harrow is 3 weeks. Our managed properties average just 8 days because:
- We start marketing 8 weeks before tenancy ends
- Pre-qualified tenant database of 500+ active seekers
- Flexible viewing times including evenings and weekends

### Ready to Maximize Your Returns?

Contact Harry & Jacobs for a free rental valuation. We'll show you exactly how much your property could earn.

[Get Your Free Valuation](/landlord/free-valuation)
    `
  },
  {
    id: 'landlord-tax-changes-2025-what-you-need-to-know',
    title: 'Landlord Tax Changes 2025: What You Need to Know',
    excerpt: 'Major tax changes affecting landlords in 2025. From mortgage interest relief to Capital Gains Tax updates, here\'s how to protect your investment.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-02-03',
    readTime: '10 min read',
    image: 'https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=800&q=80',
    tags: ['Tax', 'Landlord', 'Finance', 'Property Investment'],
    featured: false,
    content: `
## 2025 Tax Updates Every Landlord Must Know

The tax landscape for landlords continues to evolve. Here's your comprehensive guide to staying compliant and tax-efficient.

### Mortgage Interest Relief: The Current State

Since April 2020, mortgage interest is no longer deductible from rental income. Instead, you receive a 20% tax credit. This particularly affects higher-rate taxpayers.

**Example:**
- Rental Income: £20,000/year
- Mortgage Interest: £8,000/year
- **Old System:** Tax on £12,000
- **New System:** Tax on £20,000, minus 20% of £8,000 (£1,600 credit)

### Capital Gains Tax (CGT) Updates

Current CGT rates on property:
- Basic rate taxpayers: 18%
- Higher rate taxpayers: 24%

**Annual Exemption 2025:** £3,000 (reduced from £6,000 in 2024)

### How to Reduce Your Tax Bill Legally

1. **Consider a Limited Company Structure**
   - Corporation tax at 25% vs. personal rates up to 45%
   - Full mortgage interest deductibility
   - Consult an accountant before restructuring

2. **Claim All Allowable Expenses**
   - Letting agent fees
   - Insurance
   - Maintenance and repairs
   - Accountancy fees
   - Travel to property (at 45p/mile)

3. **Use Your Spouse's Allowance**
   - Transfer property ownership to lower-earning spouse
   - Can save thousands in income tax

4. **Furnished Holiday Let (FHL) Status**
   - More favourable tax treatment
   - Must meet specific criteria

### Record Keeping Requirements

HMRC can investigate up to 6 years back. Keep:
- All receipts and invoices
- Bank statements
- Tenancy agreements
- Mortgage statements

### Need Help?

We work with specialist property accountants. Contact us for a referral.

[Contact Us](/contact)
    `
  },
  {
    id: 'best-areas-to-invest-in-harrow-2025',
    title: 'Best Areas to Invest in Harrow: 2025 Property Hotspots',
    excerpt: 'Where should you buy your next investment property in Harrow? We reveal the areas with highest yields, best growth potential, and strongest tenant demand.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-02-01',
    readTime: '7 min read',
    image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800&q=80',
    tags: ['Investment', 'Harrow', 'Property Hotspots', 'Buy-to-Let'],
    featured: true,
    content: `
## Harrow's Top Investment Areas for 2025

Based on our market data and 15+ years of local experience, here are the best areas for property investment in Harrow.

### 1. Wealdstone - Best for High Yields

**Average Yield:** 5.2%
**Average Price:** £350,000 (2-bed flat)
**Average Rent:** £1,500/month

Why invest here:
- Crossrail connectivity (future Elizabeth Line extension)
- Regeneration projects underway
- Strong demand from young professionals

### 2. Harrow-on-the-Hill - Best for Capital Growth

**Average Yield:** 4.1%
**Average Price:** £550,000 (3-bed house)
**Average Rent:** £1,900/month

Why invest here:
- Prestigious location near Harrow School
- Excellent schools catchment
- Period properties hold value well

### 3. Kenton - Best for Families

**Average Yield:** 4.8%
**Average Price:** £420,000 (3-bed semi)
**Average Rent:** £1,700/month

Why invest here:
- Great schools (Uxendon Manor, Priestmead)
- Quick Metropolitan line access
- Large family homes in high demand

### 4. Stanmore - Premium Market

**Average Yield:** 4.0%
**Average Price:** £650,000 (4-bed house)
**Average Rent:** £2,200/month

Why invest here:
- Affluent area, quality tenants
- Jubilee line terminus
- Large detached properties

### 5. Queensbury - Up and Coming

**Average Yield:** 5.0%
**Average Price:** £380,000 (2-bed flat)
**Average Rent:** £1,600/month

Why invest here:
- More affordable entry point
- Good transport links
- Growing young professional demographic

### Investment Checklist

Before buying, consider:
- [ ] Transport links (Metropolitan, Jubilee, Overground)
- [ ] School Ofsted ratings
- [ ] Local amenities
- [ ] Crime statistics
- [ ] Planning applications nearby

### Get Expert Local Advice

We've helped 500+ landlords build profitable portfolios in Harrow. Book a free consultation.

[Book Consultation](/contact)
    `
  },
  {
    id: 'tenant-screening-guide-avoid-bad-tenants',
    title: 'The Ultimate Tenant Screening Guide: How to Avoid Bad Tenants',
    excerpt: 'Bad tenants cost UK landlords £9 billion annually. Learn our proven 7-step screening process that has given us a 98% tenant success rate.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-01-28',
    readTime: '9 min read',
    image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&q=80',
    tags: ['Tenant Screening', 'Landlord Tips', 'Property Management'],
    featured: false,
    content: `
## Why Tenant Screening Matters

A bad tenant can cost you:
- 6+ months unpaid rent (£9,000-£15,000)
- Property damage (£2,000-£10,000+)
- Legal fees for eviction (£3,000-£5,000)
- Stress and lost time (priceless)

Our screening process has achieved a 98% tenant success rate over 15 years.

### The 7-Step Screening Process

#### Step 1: Application Form
Collect comprehensive information:
- Full name and date of birth
- Current and previous addresses (3 years)
- Employment details
- Income verification
- References (employer + previous landlord)

#### Step 2: Credit Check
Look for:
- CCJs (County Court Judgments)
- Bankruptcies or IVAs
- Previous evictions
- Payment history

**Red Flags:** Multiple missed payments, recent CCJs, undisclosed debts

#### Step 3: Employment Verification
Contact employer directly to verify:
- Employment status
- Length of service
- Salary (should be 2.5x rent minimum)
- Likelihood of continued employment

#### Step 4: Previous Landlord Reference
Ask previous landlord:
- Did they pay rent on time?
- Did they maintain the property?
- Were there any complaints?
- Would you rent to them again?

**Pro Tip:** Always verify you're speaking to the actual landlord, not a friend posing as one.

#### Step 5: Right to Rent Check
Legal requirement since 2016:
- Check passport or visa
- Verify immigration status
- Keep copies for 1 year after tenancy

#### Step 6: Meet in Person
Trust your instincts:
- Do they seem genuine?
- Do they ask sensible questions?
- Are they respectful and punctual?

#### Step 7: Guarantor (if needed)
For tenants with weaker profiles:
- Must be UK homeowner
- Credit check the guarantor
- Guarantor deed must be properly executed

### Red Flags to Watch For

🚩 Rushing to move in immediately
🚩 Offering to pay several months upfront (could indicate poor credit)
🚩 Reluctance to provide references
🚩 Inconsistent information
🚩 Unable to provide ID

### Let Us Handle Screening

Our comprehensive tenant vetting is included in all management packages. We find you quality tenants, guaranteed.

[Get a Management Quote](/landlord/services)
    `
  },
  {
    id: 'epc-regulations-2025-landlord-guide',
    title: 'EPC Regulations 2025: What Landlords Must Do Now',
    excerpt: 'New EPC requirements are coming. Learn what rating you need, how to improve your score, and avoid fines of up to £30,000.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-01-25',
    readTime: '6 min read',
    image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&q=80',
    tags: ['EPC', 'Regulations', 'Energy Efficiency', 'Landlord'],
    featured: false,
    content: `
## EPC Requirements for Landlords

Energy Performance Certificates are now more important than ever for landlords. Here's what you need to know.

### Current Requirements

**Minimum EPC Rating:** E (since April 2020)
**Fine for Non-Compliance:** Up to £5,000

### Proposed Future Requirements

**2028:** Minimum rating of C for new tenancies
**2030:** Minimum rating of C for all tenancies

### How to Improve Your EPC Rating

| Improvement | EPC Points | Typical Cost | Savings/Year |
|-------------|------------|--------------|--------------|
| Loft Insulation | +10-15 | £300-£500 | £150-£250 |
| Cavity Wall Insulation | +10-15 | £500-£1,500 | £150-£300 |
| LED Lighting | +5-8 | £100-£200 | £50-£75 |
| Smart Thermostat | +3-5 | £150-£250 | £75-£150 |
| Double Glazing | +10-15 | £3,000-£7,000 | £100-£200 |
| New Boiler | +10-20 | £2,000-£4,000 | £200-£350 |

### Quick Wins for Better EPC

1. **Draught-proofing** - £50-£100, instant improvement
2. **Hot water tank jacket** - £15, saves £40/year
3. **Low-energy lighting** - £5 per bulb
4. **Thermostatic radiator valves** - £100-£200

### Grants Available

- **ECO4 Scheme:** Free insulation for eligible properties
- **Boiler Upgrade Scheme:** £5,000-£6,000 towards heat pumps

### Need an EPC Assessment?

We can arrange EPC assessments and recommend cost-effective improvements.

[Contact Us](/contact)
    `
  },
  {
    id: 'how-to-handle-tenant-disputes-legally',
    title: 'How to Handle Tenant Disputes Legally: A Landlord\'s Guide',
    excerpt: 'From noise complaints to deposit disputes, learn how to handle common tenant issues legally and protect yourself from costly tribunals.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-01-20',
    readTime: '8 min read',
    image: 'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&q=80',
    tags: ['Tenant Disputes', 'Legal', 'Property Management', 'Landlord'],
    featured: false,
    content: `
## Common Tenant Disputes and How to Resolve Them

Every landlord will face disputes eventually. Here's how to handle them professionally and legally.

### 1. Late Rent Payments

**Prevention:**
- Set up standing orders from day one
- Send rent reminders 3 days before due date
- Use rent guarantee insurance

**Resolution Steps:**
1. Informal reminder (day 1-3 late)
2. Written notice (day 7)
3. Formal letter before action (day 14)
4. Begin Section 8 process (day 21+)

### 2. Property Damage

**Prevention:**
- Comprehensive inventory with photos
- Regular property inspections (quarterly)
- Clear tenancy agreement clauses

**Resolution:**
- Document damage with photos
- Get repair quotes
- Deduct from deposit via proper process
- Use deposit scheme arbitration if disputed

### 3. Noise Complaints

**Your Responsibilities:**
- Investigate all complaints
- Document everything
- Issue written warnings

**Resolution:**
- Mediation first
- Written warnings (keep copies)
- Ultimately, breach of tenancy if persistent

### 4. Deposit Disputes

**Legal Requirements:**
- Protect deposit within 30 days
- Provide Prescribed Information
- Use government-approved scheme

**At End of Tenancy:**
- Check-out inventory vs. check-in
- Fair wear and tear allowance
- Provide itemised deductions

### 5. Maintenance Disputes

**Your Legal Obligations:**
- Structure and exterior
- Heating and hot water
- Gas and electrical safety
- Sanitation

**Response Times:**
- Emergency (no heating/water): 24 hours
- Urgent (broken lock): 48 hours
- Routine: 28 days

### Documentation is Key

Always keep:
- Copies of all communications
- Photos with timestamps
- Signed acknowledgments
- Witness statements if relevant

### When to Use Our Dispute Resolution

Our management service includes:
- Mediation between parties
- Legal document preparation
- Tribunal representation
- 24/7 emergency response

[Learn About Our Management Service](/landlord/services)
    `
  },
  {
    id: 'rental-yield-calculator-explained',
    title: 'Rental Yield Calculator: How to Calculate Your Property\'s True Returns',
    excerpt: 'Learn to calculate gross yield, net yield, and ROI properly. Plus, use our free calculator to see your property\'s true performance.',
    category: 'landlord',
    author: 'Harry & Jacobs Team',
    date: '2025-01-15',
    readTime: '5 min read',
    image: 'https://images.unsplash.com/photo-1554224155-8d04cb21cd6e?w=800&q=80',
    tags: ['Rental Yield', 'Calculator', 'Investment', 'Landlord'],
    featured: false,
    content: `
## Understanding Rental Yield

Rental yield is the most important metric for property investors. Here's how to calculate it properly.

### Gross Yield

The simplest calculation:

**Formula:** (Annual Rent ÷ Property Value) × 100

**Example:**
- Monthly Rent: £1,500
- Annual Rent: £18,000
- Property Value: £400,000
- **Gross Yield: 4.5%**

### Net Yield (More Accurate)

Accounts for expenses:

**Formula:** ((Annual Rent - Annual Costs) ÷ Property Value) × 100

**Typical Costs:**
- Mortgage interest
- Management fees (10-15%)
- Maintenance (1% of value/year)
- Insurance (£150-£300/year)
- Void periods (4% of rent)
- Ground rent/service charge

**Example:**
- Annual Rent: £18,000
- Annual Costs: £6,000
- Net Rental Income: £12,000
- Property Value: £400,000
- **Net Yield: 3.0%**

### Return on Investment (ROI)

For leveraged purchases:

**Formula:** (Net Rental Income ÷ Cash Invested) × 100

**Example:**
- Property Value: £400,000
- Deposit (25%): £100,000
- Stamp Duty: £10,000
- Other Costs: £5,000
- Total Cash Invested: £115,000
- Net Rental Income: £12,000
- **Cash-on-Cash ROI: 10.4%**

### What's a Good Yield?

| Area Type | Gross Yield | Net Yield |
|-----------|-------------|-----------|
| Prime London | 2-3% | 1-2% |
| Outer London | 4-5% | 2.5-3.5% |
| Harrow | 4.5-5.5% | 3-4% |
| Regional Cities | 6-8% | 4-6% |

### Get Your Property Valued

Find out your property's actual yield with a free valuation from Harry & Jacobs.

[Free Valuation](/landlord/free-valuation)
    `
  },
  // ARCHITECTURE POSTS (3)
  {
    id: 'planning-permission-guide-2025',
    title: 'Planning Permission in Harrow 2025: Complete Guide to Getting Approved',
    excerpt: 'Navigate the planning process successfully. Learn what needs permission, how to apply, and our tips for a 95% approval rate.',
    category: 'architecture',
    author: 'Harry & Jacobs Architecture',
    date: '2025-02-04',
    readTime: '12 min read',
    image: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&q=80',
    tags: ['Planning Permission', 'Harrow Council', 'Architecture', 'Extensions'],
    featured: true,
    content: `
## Planning Permission in Harrow: Everything You Need to Know

Getting planning permission can seem daunting, but with the right approach, approval rates can exceed 95%. Here's our comprehensive guide.

### What Needs Planning Permission?

**Generally Requires Permission:**
- Extensions beyond Permitted Development
- Loft conversions with dormer windows
- New buildings
- Change of use
- Listed building alterations
- Conservation area works

**Usually Permitted Development:**
- Single-storey rear extensions (up to 4m/8m)
- Loft conversions without dormers
- Porches under 3m²
- Outbuildings under 2.5m height

### Harrow Council Planning Fees 2025

| Application Type | Fee |
|------------------|-----|
| Householder | £258 |
| Full Application | £578+ |
| Change of Use | £578 |
| Listed Building | Free |
| Lawful Development Certificate | £129 |

### The Application Process

**Week 1-2: Pre-Application**
- Pre-application advice from Harrow Council (£150-£500)
- Identify constraints (conservation, flood zone, trees)
- Commission architectural drawings

**Week 3-4: Submission**
- Complete application form
- Submit plans and documents
- Pay fee

**Week 5-12: Determination**
- 8 weeks for standard applications
- 13 weeks for major applications
- Neighbour consultation period

**Common Documents Required:**
- Location plan (1:1250)
- Site plan (1:500)
- Existing and proposed floor plans
- Elevations
- Design and Access Statement
- Tree survey (if applicable)

### How to Maximize Approval Chances

1. **Pre-Application Advice**
   - Worth every penny
   - Identifies issues early
   - Shows officers you're serious

2. **Quality Drawings**
   - Use qualified architects
   - Clear, accurate, compliant
   - Show context and neighbouring properties

3. **Neighbour Consultation**
   - Speak to neighbours before applying
   - Address concerns proactively
   - Written support helps

4. **Design Sensitivity**
   - Respect local character
   - Match materials
   - Consider impact on neighbours

5. **Planning Policy Compliance**
   - Reference Local Plan policies
   - Address any conflicts
   - Show how you meet requirements

### Common Reasons for Refusal

🚫 Overshadowing neighbours
🚫 Overbearing appearance
🚫 Loss of privacy
🚫 Out of character with area
🚫 Highway safety concerns
🚫 Loss of amenity space

### Harrow-Specific Considerations

- **Conservation Areas:** Harrow-on-the-Hill, Pinner Village
- **Green Belt:** Northern parts of borough
- **Article 4 Areas:** Additional restrictions apply
- **Metropolitan Open Land:** Protected areas

### Our Planning Success Rate

Harry & Jacobs Architecture achieves a **95% approval rate** because:
- Deep knowledge of Harrow planning policies
- Strong relationships with planning officers
- Quality architectural design
- Thorough pre-application research

### Get Expert Help

From concept to approval, we handle everything.

[Book a Free Consultation](/architecture/quote)
    `
  },
  {
    id: 'loft-conversion-add-value-property',
    title: 'Loft Conversions: Add £50,000-£100,000 to Your Property Value',
    excerpt: 'A loft conversion is the most cost-effective way to add value to your home. Learn the types, costs, and how much value you could add.',
    category: 'architecture',
    author: 'Harry & Jacobs Architecture',
    date: '2025-01-30',
    readTime: '10 min read',
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800&q=80',
    tags: ['Loft Conversion', 'Home Improvement', 'Property Value', 'Architecture'],
    featured: true,
    content: `
## Loft Conversions: The Smart Investment

A loft conversion typically adds 20-25% to your property's value while costing 50-70% less than moving to a larger home.

### Value Added by Type

| Conversion Type | Cost | Value Added | ROI |
|-----------------|------|-------------|-----|
| Velux/Rooflight | £20,000-£30,000 | £30,000-£45,000 | 50% |
| Dormer (Rear) | £35,000-£50,000 | £50,000-£75,000 | 50% |
| L-Shaped Dormer | £45,000-£65,000 | £70,000-£100,000 | 55% |
| Hip-to-Gable | £50,000-£70,000 | £80,000-£110,000 | 60% |
| Mansard | £60,000-£80,000 | £90,000-£130,000 | 65% |

### Types of Loft Conversion

**1. Velux/Rooflight**
- Simplest option
- No external changes
- Usually Permitted Development
- Best for: Budget conversions, minimal disruption

**2. Rear Dormer**
- Most popular choice
- Maximum headroom
- Often Permitted Development
- Best for: Master bedrooms, en-suites

**3. L-Shaped Dormer**
- Combines rear and side dormers
- Maximum floor space
- Usually needs planning permission
- Best for: Large bedrooms, two rooms

**4. Hip-to-Gable**
- Converts hipped roof to gable
- Significant extra space
- Needs planning permission
- Best for: 1930s semi-detached houses

**5. Mansard**
- Complete roof transformation
- Premium finish
- Always needs planning permission
- Best for: High-end properties, conservation areas

### Is Your Loft Suitable?

**Minimum Requirements:**
- Head height: 2.2m at highest point
- Floor joists: Usually need strengthening
- Roof structure: Depends on type

**Ideal Conditions:**
- 2.4m+ head height
- Traditional cut roof (not trussed)
- Good access for staircase

### The Process Timeline

**Week 1-2:** Design & drawings
**Week 3-6:** Planning permission (if needed)
**Week 7-8:** Building control & party wall
**Week 9-10:** Structural preparation
**Week 11-16:** Main construction
**Week 17-18:** Finishing & snagging

**Total: 4-5 months** from design to completion

### Building Regulations Requirements

Your loft conversion must meet:
- Fire safety (escape windows, doors, alarms)
- Structural stability
- Insulation standards
- Sound insulation (if flat below)
- Electrical safety
- Staircase regulations

### Planning Permission in Harrow

**Permitted Development allows:**
- Up to 50m³ additional volume (detached)
- Up to 40m³ additional volume (semi/terrace)
- Dormers on rear only
- No higher than existing roof

**You NEED planning for:**
- Front dormers
- Higher than existing roof
- Listed buildings
- Conservation areas
- Article 4 areas

### Why Choose Harry & Jacobs

- **Turnkey Service:** Design, planning, construction
- **Fixed Price Quotes:** No hidden costs
- **15+ Years Experience:** 200+ loft conversions completed
- **RIBA Registered:** Architects you can trust

### Get a Free Quote

See how much a loft conversion could add to your property.

[Get Free Quote](/architecture/quote)
    `
  },
  {
    id: 'house-extension-ideas-add-property-value',
    title: 'House Extension Ideas That Add the Most Value to Your Property',
    excerpt: 'Not all extensions are created equal. Discover which extensions give the best ROI and how much they could add to your Harrow property.',
    category: 'architecture',
    author: 'Harry & Jacobs Architecture',
    date: '2025-01-22',
    readTime: '11 min read',
    image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=800&q=80',
    tags: ['House Extension', 'Property Value', 'Architecture', 'Home Improvement'],
    featured: false,
    content: `
## House Extensions: Maximize Your Property Value

The right extension can transform your home AND be a great investment. Here's how different extensions compare.

### Value Added by Extension Type

| Extension Type | Typical Cost | Value Added | ROI |
|----------------|--------------|-------------|-----|
| Single Storey Rear | £30,000-£60,000 | £40,000-£80,000 | 35-50% |
| Double Storey | £60,000-£120,000 | £100,000-£180,000 | 50-65% |
| Side Return | £40,000-£70,000 | £60,000-£100,000 | 45-55% |
| Wraparound | £70,000-£120,000 | £100,000-£160,000 | 45-55% |
| Basement | £150,000-£300,000 | £200,000-£500,000 | 35-70% |
| Garage Conversion | £15,000-£25,000 | £30,000-£50,000 | 80-100% |

### 1. Single Storey Rear Extension

**Best for:** Open-plan kitchen-diners, garden rooms

**Permitted Development:** Up to 4m (semi) or 8m (detached)

**Value boost:** 5-10% of property value

**Design tips:**
- Bi-fold doors to garden
- Roof lantern for light
- Underfloor heating
- Open-plan layout

### 2. Double Storey Extension

**Best for:** Growing families, work-from-home space

**Usually needs:** Planning permission

**Value boost:** 15-25% of property value

**Design tips:**
- Match existing architecture
- Consider neighbour impact
- Add en-suite to bedroom
- Future-proof layout

### 3. Side Return Extension

**Best for:** Victorian/Edwardian terraces

**Permitted Development:** Usually allowed

**Value boost:** 8-15% of property value

**Design tips:**
- Maximize kitchen space
- Glazed roof for light
- Seamless indoor-outdoor flow

### 4. Wraparound Extension

**Best for:** Maximum space gain

**Usually needs:** Planning permission

**Value boost:** 15-20% of property value

**Design tips:**
- L-shaped or U-shaped layout
- Multiple zones (kitchen, dining, living)
- Strategic window placement

### 5. Basement Conversion

**Best for:** Prime London properties, limited garden

**Always needs:** Planning permission + party wall agreements

**Value boost:** Up to 30% of property value

**Design tips:**
- Light wells essential
- Waterproofing critical
- Consider headroom requirements

### 6. Garage Conversion

**Best for:** Quick, affordable extra space

**Permitted Development:** Usually (if internal work only)

**Value boost:** 10-20% of property value

**Design tips:**
- Insulate properly
- Consider parking impact
- Make it blend with house

### What Adds Most Value in Harrow?

Based on our local market experience:

1. **Extra bedroom (especially 3rd or 4th):** +10-15%
2. **En-suite bathroom:** +5-7%
3. **Open-plan kitchen-diner:** +5-10%
4. **Home office:** +3-5%
5. **Utility room:** +2-3%

### Costs Breakdown

**Typical cost per m²:**
- Basic build: £1,800-£2,200
- Mid-range finish: £2,200-£2,800
- High-end finish: £2,800-£4,000

**Additional costs:**
- Architect fees: 5-10% of build cost
- Planning permission: £258
- Building control: £500-£1,500
- Party wall surveyors: £1,000-£3,000

### Planning Considerations in Harrow

**Usually Permitted Development:**
- Single storey up to 4m projection
- Maximum height 4m (3m within 2m of boundary)
- Not forward of front elevation
- Not more than half the garden

**Usually Needs Planning:**
- Two storeys
- Front extensions
- Conservation areas
- Listed buildings

### Our End-to-End Service

From initial sketch to final handover:

1. Free consultation & site survey
2. Concept designs & 3D visuals
3. Planning application
4. Building regulations
5. Contractor tendering
6. Project management
7. Final inspection

[Get Your Free Design Consultation](/architecture/quote)
    `
  }
];

export const Blog = () => {
  const { isArabic } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [blogPosts, setBlogPosts] = useState(staticBlogPosts);
  const [loading, setLoading] = useState(true);

  // Fetch blog posts from API
  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await fetch(`${API_URL}/api/blog/posts?published_only=true`);
        if (response.ok) {
          const posts = await response.json();
          // Merge dynamic posts with static posts, dynamic posts first
          if (posts.length > 0) {
            // Format dynamic posts to match static post structure
            const formattedPosts = posts.map(post => ({
              ...post,
              id: post.slug || post.id,
              date: post.published_at || post.created_at || new Date().toISOString(),
              readTime: post.read_time || '5 min read'
            }));
            setBlogPosts([...formattedPosts, ...staticBlogPosts]);
          }
        }
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const filteredPosts = blogPosts.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         (post.tags || []).some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = activeCategory === 'all' || post.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const featuredPosts = blogPosts.filter(post => post.featured);
  const landlordPosts = blogPosts.filter(post => post.category === 'landlord');
  const architecturePosts = blogPosts.filter(post => post.category === 'architecture');

  return (
    <div className="min-h-screen bg-gray-50 pt-20" dir={isArabic ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#404040] to-[#333] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Property Insights & Expert Advice
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Expert guidance for landlords, investors, and homeowners in Harrow
            </p>
            
            {/* Search */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 py-6 text-lg bg-white text-gray-800 border-none rounded-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b sticky top-[72px] z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-4 overflow-x-auto">
            {[
              { id: 'all', label: 'All Articles', icon: FileText },
              { id: 'landlord', label: 'For Landlords', icon: Home },
              { id: 'architecture', label: 'Architecture & Planning', icon: Building }
            ].map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full whitespace-nowrap transition-all ${
                  activeCategory === cat.id
                    ? 'bg-[#FF8C42] text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {activeCategory === 'all' && searchTerm === '' && (
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold text-[#404040] mb-6 flex items-center gap-2">
              <TrendingUp className="w-6 h-6 text-[#FF8C42]" />
              Featured Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.slice(0, 3).map(post => (
                <Link to={`/blog/${post.id}`} key={post.id}>
                  <Card className="h-full hover:shadow-xl transition-shadow overflow-hidden group">
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute top-3 left-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          post.category === 'landlord' 
                            ? 'bg-[#FF8C42] text-white' 
                            : 'bg-blue-500 text-white'
                        }`}>
                          {post.category === 'landlord' ? 'Landlord' : 'Architecture'}
                        </span>
                      </div>
                    </div>
                    <CardContent className="p-5">
                      <h3 className="font-bold text-lg text-[#404040] mb-2 group-hover:text-[#FF8C42] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{post.excerpt}</p>
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2">
              <h2 className="text-xl font-bold text-[#404040] mb-6">
                {activeCategory === 'all' ? 'Latest Articles' : 
                 activeCategory === 'landlord' ? 'Landlord Guides' : 'Architecture & Planning'}
                {searchTerm && ` matching "${searchTerm}"`}
              </h2>
              
              {filteredPosts.length === 0 ? (
                <Card className="p-8 text-center">
                  <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p className="text-gray-500">No articles found matching your search.</p>
                </Card>
              ) : (
                <div className="space-y-6">
                  {filteredPosts.map(post => (
                    <Link to={`/blog/${post.id}`} key={post.id}>
                      <Card className="hover:shadow-lg transition-shadow overflow-hidden group">
                        <div className="flex flex-col md:flex-row">
                          <div className="md:w-1/3 h-48 md:h-auto overflow-hidden">
                            <img 
                              src={post.image} 
                              alt={post.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                            />
                          </div>
                          <CardContent className="flex-1 p-5">
                            <div className="flex items-center gap-2 mb-2">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                post.category === 'landlord' 
                                  ? 'bg-orange-100 text-orange-700' 
                                  : 'bg-blue-100 text-blue-700'
                              }`}>
                                {post.category === 'landlord' ? 'Landlord' : 'Architecture'}
                              </span>
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {post.readTime}
                              </span>
                            </div>
                            <h3 className="font-bold text-lg text-[#404040] mb-2 group-hover:text-[#FF8C42] transition-colors">
                              {post.title}
                            </h3>
                            <p className="text-gray-600 text-sm mb-3 line-clamp-2">{post.excerpt}</p>
                            <div className="flex items-center justify-between">
                              <span className="text-xs text-gray-500 flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                {new Date(post.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </span>
                              <span className="text-[#FF8C42] text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                                Read More <ArrowRight className="w-4 h-4" />
                              </span>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* CTA Card */}
              <Card className="bg-gradient-to-br from-[#FF8C42] to-[#FF6B35] text-white">
                <CardContent className="p-6">
                  <PoundSterling className="w-10 h-10 mb-4" />
                  <h3 className="font-bold text-xl mb-2">Free Property Valuation</h3>
                  <p className="text-sm opacity-90 mb-4">
                    Find out how much your property could earn or sell for.
                  </p>
                  <Link to="/valuation">
                    <Button className="w-full bg-white text-[#FF8C42] hover:bg-gray-100">
                      Get Free Valuation
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Popular Tags */}
              <Card>
                <CardContent className="p-5">
                  <h3 className="font-bold text-[#404040] mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-[#FF8C42]" />
                    Popular Topics
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {['Rental Income', 'Property Investment', 'Planning Permission', 'Tax', 'EPC', 'Loft Conversion', 'Harrow'].map(tag => (
                      <button
                        key={tag}
                        onClick={() => setSearchTerm(tag)}
                        className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm hover:bg-[#FF8C42] hover:text-white transition-colors"
                      >
                        {tag}
                      </button>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Architecture CTA */}
              <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <Building className="w-10 h-10 mb-4" />
                  <h3 className="font-bold text-xl mb-2">Planning Your Extension?</h3>
                  <p className="text-sm opacity-90 mb-4">
                    Get expert architectural advice and a free quote.
                  </p>
                  <Link to="/architecture/quote">
                    <Button className="w-full bg-white text-blue-600 hover:bg-gray-100">
                      Get Free Quote
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-12 bg-[#404040]">
        <div className="max-w-3xl mx-auto px-4 text-center text-white">
          <h2 className="text-2xl font-bold mb-4">Stay Updated</h2>
          <p className="text-gray-300 mb-6">
            Get the latest property insights and market updates delivered to your inbox.
          </p>
          <div className="flex gap-3 max-w-md mx-auto">
            <Input 
              type="email" 
              placeholder="Enter your email" 
              className="flex-1 bg-white text-gray-800 border-none"
            />
            <Button className="bg-[#FF8C42] hover:bg-[#FF6B35]">
              Subscribe
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Blog;
